import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import API from "../../../Network/API";
import { useCurrency } from "../../../services/currency";
import { PLAN_HIGHLIGHTS } from "./Subscription";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Room Rent checkout: plan summary + Stripe's embedded payment form (card, wallets, 3-D Secure).
export default function SubscriptionCheckout() {
    const { t } = useTranslation();
    const { currency, formatPrice } = useCurrency();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.authReducer);
    const prefix = location.pathname.startsWith("/app") ? "/app" : "";
    const packageId = new URLSearchParams(location.search).get("package");

    const [plan, setPlan] = useState(null);
    const [error, setError] = useState(null);
    const [checkoutOptions, setCheckoutOptions] = useState(null);

    useEffect(() => {
        if (user?.is_subscribed) return;
        API.get(`/v1/oauth/teacher/packages`).then((response) => {
            const packages = response.data?.data?.packages || [];
            const chosen = packages.find((p) => String(p.id) === String(packageId)) || packages[0];
            if (!chosen) {
                setError(t("subscriptionCheckout.plan_not_found"));
                return;
            }
            setPlan(chosen);
            const completePath = `${prefix}/dashboard/subscription/complete`;
            return API.post(`/v1/oauth/teacher/subscription/checkout-session`, {
                package_id: chosen.id,
                return_url: window.location.origin + completePath,
            }).then((res) => {
                const { clientSecret, sessionId } = res.data;
                setCheckoutOptions({
                    clientSecret,
                    // cards and wallets finish here; redirect-based methods come back via return_url
                    onComplete: () => navigate(`${completePath}?session_id=${sessionId}`),
                });
            });
        }).catch((err) => {
            setError(err?.response?.data?.message || t("subscriptionCheckout.load_failed"));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packageId]);

    // Options must keep the same identity for the embedded form.
    const options = useMemo(() => checkoutOptions, [checkoutOptions]);

    if (user?.is_subscribed) {
        return (
            <div className="sco-state">
                <i className="fa-solid fa-circle-check" />
                <h4>{t("subscriptionCheckout.already_subscribed")}</h4>
                <Link to={`${prefix}/dashboard/subscription`} className="btn subscribe-btn">{t("subscriptionCheckout.view_plan")}</Link>
            </div>
        );
    }

    return (
        <div className="sco-page">
            <Link to={`${prefix}/dashboard/subscription`} className="sco-back">
                <i className="fa-light fa-arrow-left me-2" />{t("subscriptionCheckout.back_to_plans")}
            </Link>

            <div className="sco-grid">
                <aside className="sco-summary">
                    <span className="sco-badge"><i className="fa-light fa-crown me-2" />{t("subscription.room_rent_monthly_plan")}</span>
                    <h3 className="sco-plan">{plan?.title || t("subscriptionCheckout.room_rent")}</h3>
                    <div className="sco-price">
                        <strong>{plan ? `$${plan.price}` : "—"}</strong>
                        <span>{t("subscription.per_month")}</span>
                    </div>
                    {plan && currency !== "USD" &&
                        <p className="sco-approx">{t("subscriptionCheckout.approx_local", { amount: formatPrice(plan.price) })}</p>
                    }

                    <ul className="sco-features">
                        {PLAN_HIGHLIGHTS.map((f) => (
                            <li key={f.text}><i className="fa-solid fa-circle-check" /><span>{t(f.text)}</span></li>
                        ))}
                    </ul>

                    <div className="sco-lines">
                        <div><span>{t("subscriptionCheckout.billed")}</span><span>{t("subscriptionCheckout.monthly")}</span></div>
                        <div className="sco-total"><span>{t("subscriptionCheckout.due_today")}</span><span>{plan ? `$${plan.price}` : "—"}</span></div>
                    </div>

                    <p className="sco-secure"><i className="fa-solid fa-shield-check" />{t("subscriptionCheckout.secure_note")}</p>
                </aside>

                <section className="sco-pay">
                    <h4 className="sco-pay-title">{t("subscriptionCheckout.payment_details")}</h4>
                    {error ?
                        <div className="sco-error">
                            <i className="fa-light fa-circle-exclamation" />
                            <span>{error}</span>
                            <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => window.location.reload()}>{t("subscriptionCheckout.retry")}</button>
                        </div>
                        : options ?
                            <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                            :
                            <div className="sco-loading">
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                {t("subscriptionCheckout.preparing")}
                            </div>
                    }
                </section>
            </div>
        </div>
    );
}
